import React from 'react';
import {motion} from 'framer-motion';
import {useInView} from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import ChatInterface from './ChatInterface';

const {FiSearch,FiFrown,FiHeart}=FiIcons;

const ProblemSolution=()=> {
  const [ref,inView]=useInView({threshold: 0.1,triggerOnce: true});
  
  const containerVariants={
    hidden: {opacity: 0},
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants={
    hidden: {opacity: 0,y: 30},
    visible: {
      opacity: 1,
      y: 0,
      transition: {duration: 0.6,ease: 'easeOut'},
    },
  };

  return (
    <section ref={ref} className="py-24 bg-white" aria-labelledby="problem-solution-heading">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mobile: Chat Interface First */}
        <motion.div variants={containerVariants} initial="hidden" animate={inView ? "visible" : "hidden"} className="lg:hidden space-y-16">
          {/* Chat Interface */}
          <motion.div variants={itemVariants}>
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
                <div className="bg-purple-600 text-white p-6">
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiSearch} className="w-6 h-6" />
                    <h3 className="text-xl font-semibold">Queer Discovery Chat</h3>
                  </div>
                </div>
                <div className="h-80 overflow-y-auto p-6 space-y-4 bg-gray-50">
                  <div className="flex justify-start">
                    <div className="max-w-xs p-4 rounded-2xl bg-white border border-gray-200">
                      <p className="text-sm text-gray-900">Hi! I'm here to help you discover queer events and businesses near you. What are you looking for today? 🌈</p>
                      <p className="text-xs text-gray-500 mt-2">Just now</p>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="max-w-xs p-4 rounded-2xl bg-purple-600 text-white">
                      <p className="text-sm">Looking for queer events in London this weekend</p>
                      <p className="text-xs text-purple-200 mt-2">Just now</p>
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="max-w-xs p-4 rounded-2xl bg-white border border-gray-200">
                      <p className="text-sm text-gray-900">Great! I found several amazing events for you. There's a queer book club tomorrow, Pride planning on Saturday and a drag show Sunday evening. Would you like details?</p>
                      <p className="text-xs text-gray-500 mt-2">Just now</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 border-t border-gray-200 bg-white">
                  <div className="flex space-x-3">
                    <input 
                      type="text" 
                      placeholder="Ask about queer events or businesses..." 
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-600 focus:border-transparent text-sm" 
                      readOnly 
                    />
                    <button className="bg-purple-600 text-white p-3 rounded-xl hover:bg-purple-700 transition-colors">
                      <SafeIcon icon={FiSearch} className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
              {/* Floating elements */}
              <motion.div 
                animate={{y: [-10,10,-10]}} 
                transition={{duration: 4,repeat: Infinity,ease: "easeInOut"}} 
                className="absolute -top-4 -right-4 bg-primary text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg" 
              >
                💬 Chat-Powered
              </motion.div>
              <motion.div 
                animate={{y: [10,-10,10]}} 
                transition={{duration: 5,repeat: Infinity,ease: "easeInOut"}} 
                className="absolute -bottom-4 -left-4 bg-accent-2 text-white px-3 py-2 rounded-full text-sm font-semibold shadow-lg" 
              >
                🌈 LGBTQIA+ Focused
              </motion.div>
            </div>
          </motion.div>

          {/* Content Section */}
          <motion.div variants={itemVariants} className="space-y-8">
            {/* Problem Section */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-3 bg-red-100 rounded-xl">
                  <FiFrown className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">
                  Tired of scrolling through outdated listings and scattered directories?
                </h2>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed">
                We know how hard it can be to find genuinely inclusive, queer-centred events and spaces — especially when you're short on time or energy. That's why we created a better way.
              </p>
            </div>

            {/* Solution Section */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-3 bg-green-100 rounded-xl">
                  <FiHeart className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Our Solution</h3>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our chat-powered discovery tool helps you quickly uncover verified queer events and businesses near you — all through a simple, trusted conversation. No spam. No digging. Just support for your community, in your language and on your terms.
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Desktop: Side by Side Layout */}
        <motion.div variants={containerVariants} initial="hidden" animate={inView ? "visible" : "hidden"} className="hidden lg:block">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Chat Interface */}
            <motion.div variants={itemVariants} className="order-2 lg:order-1">
              <div className="relative">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
                  <div className="bg-purple-600 text-white p-6">
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiSearch} className="w-6 h-6" />
                      <h3 className="text-xl font-semibold">Queer Discovery Chat</h3>
                    </div>
                  </div>
                  <div className="h-80 overflow-y-auto p-6 space-y-4 bg-gray-50">
                    <div className="flex justify-start">
                      <div className="max-w-xs p-4 rounded-2xl bg-white border border-gray-200">
                        <p className="text-sm text-gray-900">Hi! I'm here to help you discover queer events and businesses near you. What are you looking for today? 🌈</p>
                        <p className="text-xs text-gray-500 mt-2">Just now</p>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <div className="max-w-xs p-4 rounded-2xl bg-purple-600 text-white">
                        <p className="text-sm">Looking for queer events in London this weekend</p>
                        <p className="text-xs text-purple-200 mt-2">Just now</p>
                      </div>
                    </div>
                    <div className="flex justify-start">
                      <div className="max-w-xs p-4 rounded-2xl bg-white border border-gray-200">
                        <p className="text-sm text-gray-900">Great! I found several amazing events for you. There's a queer book club tomorrow, Pride planning on Saturday and a drag show Sunday evening. Would you like details?</p>
                        <p className="text-xs text-gray-500 mt-2">Just now</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 border-t border-gray-200 bg-white">
                    <div className="flex space-x-3">
                      <input 
                        type="text" 
                        placeholder="Ask about queer events or businesses..." 
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-600 focus:border-transparent text-sm" 
                        readOnly 
                      />
                      <button className="bg-purple-600 text-white p-3 rounded-xl hover:bg-purple-700 transition-colors">
                        <SafeIcon icon={FiSearch} className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
                {/* Floating elements */}
                <motion.div 
                  animate={{y: [-10,10,-10]}} 
                  transition={{duration: 4,repeat: Infinity,ease: "easeInOut"}} 
                  className="absolute -top-4 -right-4 bg-primary text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg" 
                >
                  💬 Chat-Powered
                </motion.div>
                <motion.div 
                  animate={{y: [10,-10,10]}} 
                  transition={{duration: 5,repeat: Infinity,ease: "easeInOut"}} 
                  className="absolute -bottom-4 -left-4 bg-accent-2 text-white px-3 py-2 rounded-full text-sm font-semibold shadow-lg" 
                >
                  🌈 LGBTQIA+ Focused
                </motion.div>
              </div>
            </motion.div>

            {/* Right: Content */}
            <motion.div variants={itemVariants} className="order-1 lg:order-2 space-y-8">
              {/* Problem Section */}
              <div className="space-y-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-3 bg-red-100 rounded-xl">
                    <FiFrown className="w-6 h-6 text-red-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">
                    Tired of scrolling through outdated listings and scattered directories?
                  </h2>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed">
                  We know how hard it can be to find genuinely inclusive, queer-centred events and spaces — especially when you're short on time or energy. That's why we created a better way.
                </p>
              </div>

              {/* Solution Section */}
              <div className="space-y-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-3 bg-green-100 rounded-xl">
                    <FiHeart className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Our Solution</h3>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Our chat-powered discovery tool helps you quickly uncover verified queer events and businesses near you — all through a simple, trusted conversation. No spam. No digging. Just support for your community, in your language and on your terms.
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSolution;